import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { Star } from 'lucide-react';

interface TestimonialsProps {
  darkMode?: boolean;
}

export const Testimonials: React.FC<TestimonialsProps> = ({ darkMode = false }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Increase height to allow for a long, smooth scroll of all cards
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Text Animations
  const textScale = useTransform(smoothProgress, [0, 0.5], [1, 0.8]);
  // const textOpacity = useTransform(smoothProgress, [0.8, 0.95], [1, 0]); // Fade out near end
  
  // Slice effect
  const sliceDistance = useTransform(smoothProgress, [0.85, 1], [0, -150]); 
  const sliceDistanceDown = useTransform(smoothProgress, [0.85, 1], [0, 150]);
  const sliceOpacity = useTransform(smoothProgress, [0.9, 1], [1, 0]);

  // Cards Movement
  // We want the group of cards to traverse the screen. 
  // Start: Below view (100vh). End: Scrolled past (negative value).
  // The layout is tall, so we need a large negative value to clear it.
  const cardsY = useTransform(smoothProgress, [0, 1], ['100vh', '-180vh']);

  const testimonials = [
    {
      name: 'Sarah Connor',
      role: 'CEO, TechCorp',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      content: 'The quality of packaging from PrintAura has significantly elevated our brand perception.',
      // Custom grid position helper
      gridArea: 'left' 
    },
    {
      name: 'Michael Chen',
      role: 'Founder, EcoGoods',
      image: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      content: 'Their commitment to sustainable materials is exactly what we needed.',
      gridArea: 'right'
    },
    {
      name: 'Emily Davis',
      role: 'Director, BeautyCo',
      image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      content: 'Fast turnaround times and impeccable attention to detail.',
      gridArea: 'center'
    },
    {
      name: 'David Wilson',
      role: 'Manager, FoodInc',
      image: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      content: 'PrintAura transformed our product shelf appeal completely.',
      gridArea: 'left'
    },
    {
      name: 'Jessica Lee',
      role: 'Owner, OrganicLife',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      content: 'Professional team and outstanding print quality every time.',
      gridArea: 'right'
    },
    {
      name: 'Robert Taylor',
      role: 'VP, RetailSol',
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      content: 'Reliable partners for our high-volume packaging requirements.',
      gridArea: 'center'
    }
  ];

  return (
    <section ref={containerRef} className={`relative h-[400vh] ${darkMode ? 'bg-black' : 'bg-white'}`}>
      
      {/* Sticky Viewport */}
      <div className="sticky top-0 h-screen w-full overflow-hidden flex items-center justify-center">
        
        {/* BACKGROUND Text */}
        <div className="absolute inset-0 z-0 flex flex-col items-center justify-center pointer-events-none select-none">
           <motion.div
              style={{
                y: sliceDistance,
                opacity: sliceOpacity,
                scale: textScale,
                clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
                marginBottom: '-0.1em'
              }}
           >
              <h2 
                className="text-center whitespace-nowrap"
                style={{
                  fontFamily: 'Nanum Myeongjo',
                  fontWeight: 700,
                  fontSize: '12vw',
                  lineHeight: '1.1',
                  color: darkMode ? '#FFFFFF' : '#000000',
                  opacity: 0.1
                }}
              >
                What They <br /> Are Saying
              </h2>
           </motion.div>
           
            <motion.div
              style={{
                y: sliceDistanceDown,
                opacity: sliceOpacity,
                scale: textScale,
                clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
           >
              <h2 
                className="text-center whitespace-nowrap"
                style={{
                  fontFamily: 'Nanum Myeongjo',
                  fontWeight: 700,
                  fontSize: '12vw',
                  lineHeight: '1.1',
                  color: darkMode ? '#FFFFFF' : '#000000',
                  opacity: 0.1
                }}
              >
                What They <br /> Are Saying
              </h2>
           </motion.div>
        </div>

        {/* Testimonial Cards Container - 2-1-2-1 Grid */}
        <motion.div 
            style={{ y: cardsY }}
            className="absolute top-0 w-full max-w-6xl px-4 z-10 flex flex-col items-center gap-16 pointer-events-auto"
        >
            {/* We will construct the rows manually for the 2-1-2-1 pattern */}
            
            {/* Row 1: Left & Right */}
            <div className="flex flex-col md:flex-row w-full justify-between px-4 md:px-8 lg:px-20 gap-8 md:gap-0">
                <Card t={testimonials[0]} darkMode={darkMode} />
                <Card t={testimonials[1]} darkMode={darkMode} />
            </div>

            {/* Row 2: Center */}
            <div className="flex w-full justify-center">
                <Card t={testimonials[2]} darkMode={darkMode} />
            </div>

            {/* Row 3: Left & Right */}
            <div className="flex flex-col md:flex-row w-full justify-between px-4 md:px-8 lg:px-20 gap-8 md:gap-0">
                <Card t={testimonials[3]} darkMode={darkMode} />
                <Card t={testimonials[4]} darkMode={darkMode} />
            </div>

            {/* Row 4: Center */}
            <div className="flex w-full justify-center">
                 <Card t={testimonials[5]} darkMode={darkMode} />
            </div>

            {/* Extra padding at bottom to ensure last card scrolls fully away if needed */}
            <div className="h-[20vh]" />

        </motion.div>
      </div>
    </section>
  );
};

const Card = ({ t, darkMode }: { t: any, darkMode: boolean }) => (
    <div className={`
        w-[90vw] md:w-[350px] p-8 rounded-3xl shadow-xl border backdrop-blur-md
        ${darkMode 
            ? 'bg-zinc-900/60 border-zinc-800' // Increased opacity for readability over text
            : 'bg-white/90 border-gray-100'
        }
        transition-transform hover:scale-105 duration-300
    `}>
        <UserInfo t={t} darkMode={darkMode} />
    </div>
);

const UserInfo = ({ t, darkMode }: { t: any, darkMode: boolean }) => (
  <>
    <div className="flex items-center gap-4 mb-4">
      <img 
        src={t.image} 
        alt={t.name} 
        className="w-12 h-12 rounded-full object-cover"
      />
      <div>
        <h4 className={`font-bold text-base ${darkMode ? 'text-white' : 'text-zinc-900'}`}>{t.name}</h4>
        <p className={`text-xs ${darkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t.role}</p>
      </div>
    </div>
    <div className="flex text-orange-400 mb-3">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} size={14} fill="currentColor" />
      ))}
    </div>
    <p className={`text-sm leading-relaxed ${darkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>
      "{t.content}"
    </p>
  </>
);
